import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import type {
  CompanyChainRow,
  MappedCompany,
} from '@/api/supply-chain-mapping/types';
import { InsightsConnectionHealthCard } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsConnectionHealthCard';
import { InsightsCountriesDonut } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsCountriesDonut';
import { InsightsCriticalSuppliersBar } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsCriticalSuppliersBar';
import { InsightsFacilityProcessPie } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsFacilityProcessPie';
import { InsightsFacilityTreemap } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsFacilityTreemap';
import { InsightsHistoryBarChart } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsHistoryBarChart';
import { InsightsMappingDepthBar } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsMappingDepthBar';
import { InsightsNetworkUsageGauge } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsNetworkUsageGauge';
import { InsightsProgressDonut } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsProgressDonut';
import { InsightsRecentActiveList } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsRecentActiveList';
import { InsightsScatterChart } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsScatterChart';
import { InsightsSummaryCard } from '@/app/(client)/supply-chain-mapping/-components/insights/InsightsSummaryCard';
import { useAllMappedCompanies } from '@/app/(client)/supply-chain-mapping/-hooks/useAllMappedCompanies';
import { useAllSupplyChains } from '@/app/(client)/supply-chain-mapping/-hooks/useAllSupplyChains';
import { useCriticalSuppliers } from '@/app/(client)/supply-chain-mapping/-hooks/useCriticalSuppliers';
import { useFacilityProcessOverview } from '@/app/(client)/supply-chain-mapping/-hooks/useFacilityProcessOverview';
import { useInsightsChainHistory } from '@/app/(client)/supply-chain-mapping/-hooks/useInsightsChainHistory';
import { useSupplierStatistics } from '@/app/(client)/supply-chain-mapping/-hooks/useSupplierStatistics';
import { PageContainer } from '@/components/shared/PageContainer';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/supply-chain-mapping/(pages)/(list)/insights/overview/',
)({
  component: InsightsOverviewPage,
});

function InsightsOverviewPage() {
  const { getTranslation: t } = useTranslator({ page: 'supplyChainMapping' });

  const { data: statsRes } = useSupplierStatistics();
  const { data: historyEntries = [] } = useInsightsChainHistory();
  const { data: mappedRes } = useAllMappedCompanies();
  const { data: criticalSuppliers = [] } = useCriticalSuppliers();
  const { data: facilityItems = [] } = useFacilityProcessOverview();
  const { data: chainsRes } = useAllSupplyChains();

  const stats = statsRes;
  const mappedCompanies = (mappedRes?.data ?? []) as MappedCompany[];
  const supplyChains = (chainsRes?.data ?? []) as CompanyChainRow[];

  const countryData = useMemo(() => {
    const counts = mappedCompanies.reduce<Record<string, number>>((acc, c) => {
      const key = c.countryName ?? t('insights.unknownCountry');
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [mappedCompanies, t]);

  const totalCount = mappedCompanies.length;
  // API returns 'Not Connected' as text; old frontend compares after .toUpperCase() = 'NOT CONNECTED'
  const disconnectedCount = mappedCompanies.filter(
    c =>
      c.connectionType?.replace(/[\s_]/g, '').toUpperCase() === 'NOTCONNECTED',
  ).length;

  const progressData = useMemo(
    () => ({
      IN_PROGRESS: supplyChains.filter(
        c => c.supplyChainStatus === 'IN_PROGRESS',
      ).length,
      IN_REVIEW: supplyChains.filter(c => c.supplyChainStatus === 'IN_REVIEW')
        .length,
      COMPLETED: supplyChains.filter(c => c.supplyChainStatus === 'COMPLETED')
        .length,
    }),
    [supplyChains],
  );

  const mappingDepthData = useMemo(
    () =>
      [0, 1, 2, 3, 4].map(depth => ({
        depth,
        count: supplyChains.filter(
          c =>
            Number.parseInt(
              String(c.mappingDepth ?? '').split(' ')[0] ?? '',
              10,
            ) === depth,
        ).length,
      })),
    [supplyChains],
  );

  return (
    <PageContainer className='space-y-6 overflow-auto'>
      {/* ── KPI cards ─────────────────────────────────────────────── */}
      <div className='grid grid-cols-2 @4xl:grid-cols-4 gap-4'>
        <InsightsSummaryCard
          title={t('insights.cards.mappedSupplyChains')}
          tooltip={t('insights.cards.tooltip.mappedSupplyChains')}
          value={stats?.numberOfSupplyChainsCreated ?? 0}
          description={t('insights.cards.description.mappedSupplyChains')}
        />
        <InsightsSummaryCard
          title={t('insights.cards.totalMappedCompanies')}
          tooltip={t('insights.cards.tooltip.totalMappedCompanies')}
          value={stats?.totalNumberOfSuppliers ?? 0}
          description={t('insights.cards.description.totalMappedCompanies')}
        />
        <InsightsSummaryCard
          title={t('insights.cards.totalCollaborators')}
          tooltip={t('insights.cards.tooltip.totalCollaborators')}
          value={stats?.numberOfCollaborators ?? 0}
          description={t('insights.cards.description.totalCollaborators')}
        />
        <InsightsSummaryCard
          title={t('insights.cards.facilityProcesses')}
          tooltip={t('insights.cards.tooltip.facilityProcesses')}
          value={stats?.totalNumberOfFacilityProcesses ?? 0}
          description={t('insights.cards.description.facilityProcesses')}
        />
      </div>

      {/* Network */}
      <div className='grid grid-cols-1 @4xl:grid-cols-3 gap-4 min-h-72'>
        <div className='@4xl:col-span-2 h-full'>
          <InsightsConnectionHealthCard
            totalCount={totalCount}
            disconnectedCount={disconnectedCount}
          />
        </div>
        <InsightsNetworkUsageGauge
          excludedSuppliers={stats?.numberOfExcludedSupplier ?? 0}
          totalNumberOfCompanies={stats?.totalNumberOfCompanies ?? 0}
        />
      </div>

      {/* Activity */}
      <div className='grid grid-cols-1 @4xl:grid-cols-3 gap-4 min-h-72'>
        <InsightsRecentActiveList
          historyEntries={historyEntries}
          supplyChains={supplyChains}
        />
        <div className='@4xl:col-span-2 h-full'>
          <InsightsHistoryBarChart historyEntries={historyEntries} />
        </div>
      </div>

      {/* Supply chain analysis */}
      <div className='grid grid-cols-1 @3xl:grid-cols-2 gap-4 min-h-120'>
        <InsightsProgressDonut progressData={progressData} />
        <InsightsMappingDepthBar mappingDepthData={mappingDepthData} />
      </div>
      <div className='grid grid-cols-1 @3xl:grid-cols-2 gap-4 min-h-120'>
        <InsightsScatterChart supplyChains={supplyChains} />
        <InsightsCountriesDonut countryData={countryData} />
      </div>

      {/* Facility overview */}
      <div className='grid grid-cols-1 @4xl:grid-cols-3 gap-4 min-h-120'>
        <InsightsFacilityProcessPie
          totalFacilityProcesses={stats?.totalNumberOfFacilityProcesses ?? 0}
          exclusiveSuppliers={stats?.numberOfExclusiveSupplier ?? 0}
        />
        <div className='@4xl:col-span-2 h-full'>
          <InsightsCriticalSuppliersBar suppliers={criticalSuppliers} />
        </div>
      </div>
      <div className='grid grid-cols-1 min-h-160'>
        <InsightsFacilityTreemap items={facilityItems} />
      </div>
    </PageContainer>
  );
}
